import * as React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, IconButton, InputLabel, MenuItem, Pagination, Select, Stack, Tab, Table, TableBody, TableCell,
    TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
    getAvailableVirtualPlayers, getVirtualProviderTeam, getVirtualTeamPlayers, regenerateVirtualTeamImage,
    updateVirtualTeam,
} from '../../Api';
import {
    VirtualLineupType, VirtualProviderPlayerDto, VirtualProviderTeamDto, VirtualTeamDraftDto,
} from '../../models/dtos/VirtualProviderCatalogDto';
import {
    VIRTUAL_LINEUP_TYPES, VIRTUAL_TEAM_ROSTER_PLANS, virtualLineupLabel, virtualRosterMaximum,
} from './VirtualTeamRoster';
import VirtualCountrySelect from './VirtualCountrySelect';

const EMPTY_DRAFT: VirtualTeamDraftDto = { name: '', nameRu: '', biography: '', biographyRu: '', originCountry: '' };
const PLAYER_POOL_PAGE_SIZE = 10;

const russianTeamName = (team: VirtualProviderTeamDto): string => {
    if (!team.nameTranslationsJson) return '';
    try {
        const translations = JSON.parse(team.nameTranslationsJson) as Record<string, unknown>;
        return typeof translations.ru === 'string' ? translations.ru : '';
    } catch {
        return '';
    }
};

const teamDraft = (team: VirtualProviderTeamDto): VirtualTeamDraftDto => ({
    name: team.name,
    nameRu: russianTeamName(team),
    biography: team.biography,
    biographyRu: team.biographyRu,
    originCountry: team.originCountry ?? '',
});

const rosterAssignments = (
    teamId: string,
    players: VirtualProviderPlayerDto[],
): Record<string, VirtualLineupType> => Object.fromEntries(players.flatMap(player => {
    const membership = player.memberships.find(item => item.team.id === teamId && item.effective);
    return membership ? [[player.id, membership.lineupType]] : [];
}));

export default function VirtualTeamDetailsDialog(props: {
    team: VirtualProviderTeamDto | null;
    sportNames: Map<number, string>;
    onClose: () => void;
    onUpdated: (team: VirtualProviderTeamDto) => void;
}) {
    const [team, setTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [players, setPlayers] = React.useState<VirtualProviderPlayerDto[]>([]);
    const [availablePlayers, setAvailablePlayers] = React.useState<VirtualProviderPlayerDto[]>([]);
    const [selectedPoolPlayers, setSelectedPoolPlayers] = React.useState<Record<string, VirtualProviderPlayerDto>>({});
    const [poolPage, setPoolPage] = React.useState(0);
    const [poolTotal, setPoolTotal] = React.useState(0);
    const [assignments, setAssignments] = React.useState<Record<string, VirtualLineupType>>({});
    const [draft, setDraft] = React.useState<VirtualTeamDraftDto>(EMPTY_DRAFT);
    const [selectedPoolPlayerId, setSelectedPoolPlayerId] = React.useState('');
    const [tab, setTab] = React.useState(0);
    const [editing, setEditing] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [detailsLoaded, setDetailsLoaded] = React.useState(false);
    const [loadingPool, setLoadingPool] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [regenerating, setRegenerating] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.team) return;
        let cancelled = false;
        setTeam(props.team);
        setDraft(teamDraft(props.team));
        setPlayers([]);
        setAvailablePlayers([]);
        setSelectedPoolPlayers({});
        setPoolPage(0);
        setPoolTotal(0);
        setAssignments({});
        setSelectedPoolPlayerId('');
        setTab(0);
        setEditing(false);
        setDetailsLoaded(false);
        setError('');
        setLoading(true);
        Promise.all([getVirtualProviderTeam(props.team.id), getVirtualTeamPlayers(props.team.id)])
            .then(([loadedTeam, loadedPlayers]) => {
                if (cancelled) return;
                setTeam(loadedTeam);
                setDraft(teamDraft(loadedTeam));
                setPlayers(loadedPlayers);
                setAssignments(rosterAssignments(loadedTeam.id, loadedPlayers));
                setDetailsLoaded(true);
            })
            .catch(reason => {
                if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load team details');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [props.team]);

    if (!team) {
        return <Dialog open={props.team != null} onClose={props.onClose} fullWidth maxWidth="lg">
            <DialogTitle>Virtual team details</DialogTitle>
            <DialogContent><Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box></DialogContent>
        </Dialog>;
    }

    const rosterPlan = VIRTUAL_TEAM_ROSTER_PLANS[team.sportId];
    const assignmentCounts = VIRTUAL_LINEUP_TYPES.reduce<Record<VirtualLineupType, number>>((counts, role) => {
        counts[role] = Object.values(assignments).filter(value => value === role).length;
        return counts;
    }, { STARTING_LINEUP: 0, SUBSTITUTES: 0, COACH: 0 });
    const rosterWithinLimits = Boolean(rosterPlan) && VIRTUAL_LINEUP_TYPES.every(
        role => assignmentCounts[role] <= virtualRosterMaximum(rosterPlan, role),
    );
    const rosterComplete = Boolean(rosterPlan)
        && assignmentCounts.STARTING_LINEUP === rosterPlan.startingLineup
        && assignmentCounts.SUBSTITUTES >= rosterPlan.minimumSubstitutes
        && assignmentCounts.COACH === rosterPlan.coaches;
    const knownPlayers = new Map(
        [...players, ...Object.values(selectedPoolPlayers), ...availablePlayers].map(player => [player.id, player]),
    );
    const assignedPlayers = Object.keys(assignments)
        .map(playerId => knownPlayers.get(playerId))
        .filter((player): player is VirtualProviderPlayerDto => player != null)
        .sort((left, right) => {
            const roleDifference = VIRTUAL_LINEUP_TYPES.indexOf(assignments[left.id])
                - VIRTUAL_LINEUP_TYPES.indexOf(assignments[right.id]);
            return roleDifference || left.name.localeCompare(right.name);
        });
    const selectablePlayers = Array.from(knownPlayers.values()).filter(player => assignments[player.id] == null);
    const nextAvailableRole = VIRTUAL_LINEUP_TYPES.find(
        role => rosterPlan && assignmentCounts[role] < virtualRosterMaximum(rosterPlan, role),
    );
    const valid = draft.name.trim().length >= 3 && draft.nameRu.trim().length >= 3
        && draft.biography.trim().length >= 40 && draft.biographyRu.trim().length >= 40
        && draft.originCountry.length === 2 && rosterWithinLimits;
    const displayedRosterComplete = editing ? rosterComplete : team.isComplete;
    const busy = saving || regenerating;

    const loadAvailablePlayerPage = async (pageNumber: number) => {
        setSelectedPoolPlayerId('');
        setLoadingPool(true);
        try {
            const page = await getAvailableVirtualPlayers(
                team.sportId,
                pageNumber * PLAYER_POOL_PAGE_SIZE,
                PLAYER_POOL_PAGE_SIZE,
            );
            setAvailablePlayers(page.items);
            setPoolTotal(page.totalItems);
            setPoolPage(pageNumber);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not load the available player pool');
            setAvailablePlayers([]);
            setPoolTotal(0);
        } finally {
            setLoadingPool(false);
        }
    };

    const startEditing = async () => {
        setEditing(true);
        setError('');
        setDraft(teamDraft(team));
        setAssignments(rosterAssignments(team.id, players));
        setSelectedPoolPlayerId('');
        setSelectedPoolPlayers({});
        setPoolPage(0);
        setPoolTotal(0);
        await loadAvailablePlayerPage(0);
    };

    const cancelEditing = () => {
        setEditing(false);
        setDraft(teamDraft(team));
        setAssignments(rosterAssignments(team.id, players));
        setAvailablePlayers([]);
        setSelectedPoolPlayers({});
        setPoolPage(0);
        setPoolTotal(0);
        setSelectedPoolPlayerId('');
        setError('');
    };

    const save = async () => {
        if (!valid) return;
        setSaving(true);
        setError('');
        try {
            const updated = await updateVirtualTeam(team.id, {
                ...draft,
                poolPlayers: Object.entries(assignments).map(([playerId, lineupType]) => ({ playerId, lineupType })),
            });
            setTeam(updated.team);
            setDraft(teamDraft(updated.team));
            setPlayers(updated.players);
            setAssignments(rosterAssignments(updated.team.id, updated.players));
            setAvailablePlayers([]);
            setSelectedPoolPlayers({});
            setPoolPage(0);
            setPoolTotal(0);
            setSelectedPoolPlayerId('');
            setEditing(false);
            props.onUpdated(updated.team);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not update team');
        } finally {
            setSaving(false);
        }
    };

    const regenerate = async () => {
        setRegenerating(true);
        setError('');
        try {
            const updated = await regenerateVirtualTeamImage(team.id);
            setTeam(updated);
            setDraft(teamDraft(updated));
            props.onUpdated(updated);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not regenerate team logo');
        } finally {
            setRegenerating(false);
        }
    };

    const addSelectedPlayer = () => {
        if (!selectedPoolPlayerId || !nextAvailableRole) return;
        const selectedPlayer = knownPlayers.get(selectedPoolPlayerId);
        if (selectedPlayer) {
            setSelectedPoolPlayers(current => ({ ...current, [selectedPlayer.id]: selectedPlayer }));
        }
        setAssignments(current => ({ ...current, [selectedPoolPlayerId]: nextAvailableRole }));
        setSelectedPoolPlayerId('');
    };

    return <Dialog open={props.team != null} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="lg">
        <DialogTitle>Virtual team details</DialogTitle>
        <Box sx={{ px: 3, pb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                <Avatar src={team.imageUrl ?? undefined} variant="rounded" sx={{ width: 112, height: 112, fontSize: 32 }}>
                    {team.name.slice(0, 1)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    {editing ? <Stack spacing={2}>
                        <TextField
                            required
                            label="Name (English)"
                            value={draft.name}
                            onChange={event => setDraft(current => ({ ...current, name: event.target.value }))}
                        />
                        <TextField
                            required
                            label="Name (Russian)"
                            value={draft.nameRu}
                            onChange={event => setDraft(current => ({ ...current, nameRu: event.target.value }))}
                        />
                    </Stack> : <>
                        <Typography variant="h5">{team.name}</Typography>
                        {russianTeamName(team) ? <Typography color="text.secondary" sx={{ mt: 0.5 }}>{russianTeamName(team)}</Typography> : null}
                    </>}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        <Chip label={props.sportNames.get(team.sportId) ?? `Sport ${team.sportId}`} />
                        <Chip variant="outlined" label={(editing ? draft.originCountry : team.originCountry) || 'Country unknown'} />
                        <Chip variant="outlined" label={`Strength ${team.strengthRating}`} />
                        <Chip variant="outlined" color={team.active ? 'success' : 'default'} label={team.active ? 'Active' : 'Inactive'} />
                        <Chip
                            variant="outlined"
                            color={displayedRosterComplete ? 'success' : 'warning'}
                            label={displayedRosterComplete ? 'Complete roster' : 'Incomplete roster'}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Box>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ px: 3 }}>
            <Tab label="General" />
            <Tab label={`Players (${Object.keys(assignments).length})`} />
        </Tabs>
        <DialogContent dividers>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {loading ? <Box sx={{ py: 1, textAlign: 'center' }}><CircularProgress size={24} /></Box> : null}

            {tab === 0 ? <Stack spacing={3}>
                {editing ? <>
                    <VirtualCountrySelect
                        value={draft.originCountry}
                        onChange={originCountry => setDraft(current => ({ ...current, originCountry }))}
                        disabled={busy}
                        label="Team origin country"
                    />
                    <TextField
                        required multiline minRows={4} label="Biography (English)" value={draft.biography}
                        helperText={`${draft.biography.trim().length}/40 minimum characters`}
                        onChange={event => setDraft(current => ({ ...current, biography: event.target.value }))}
                    />
                    <TextField
                        required multiline minRows={4} label="Biography (Russian)" value={draft.biographyRu}
                        helperText={`${draft.biographyRu.trim().length}/40 minimum characters`}
                        onChange={event => setDraft(current => ({ ...current, biographyRu: event.target.value }))}
                    />
                </> : <>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">Biography (English)</Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{team.biography}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">Biography (Russian)</Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{team.biographyRu}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Created {new Date(team.createdAt).toLocaleString()} · Updated {new Date(team.updatedAt).toLocaleString()} · ID {team.id}
                    </Typography>
                </>}
            </Stack> : <Stack spacing={2}>
                {rosterPlan ? <Typography variant="body2" color={!rosterWithinLimits ? 'error.main' : rosterComplete ? 'success.main' : 'text.secondary'}>
                    Starting lineup {assignmentCounts.STARTING_LINEUP}/{rosterPlan.startingLineup} · Substitutes {assignmentCounts.SUBSTITUTES}/{rosterPlan.minimumSubstitutes} minimum ({rosterPlan.maximumSubstitutes} max) · Coach {assignmentCounts.COACH}/{rosterPlan.coaches}
                </Typography> : <Alert severity="error">This sport does not have a supported roster plan.</Alert>}

                {editing ? <Typography variant="body2" color="text.secondary">
                    Saving normalizes the roster: substitutes fill vacant lineup positions, and the oldest selected player becomes coach when none is assigned.
                </Typography> : null}

                {editing ? <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <FormControl fullWidth size="small" disabled={loadingPool || selectablePlayers.length === 0 || !nextAvailableRole}>
                        <InputLabel>Available player</InputLabel>
                        <Select
                            label="Available player"
                            value={selectedPoolPlayerId}
                            onChange={event => setSelectedPoolPlayerId(event.target.value)}
                        >
                            {selectablePlayers.map(player => <MenuItem key={player.id} value={player.id}>
                                {player.name} · {player.originCountry} · {player.yearOfBirth}
                            </MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={addSelectedPlayer}
                        disabled={!selectedPoolPlayerId || !nextAvailableRole}
                    >Add player</Button>
                    {loadingPool ? <CircularProgress size={22} /> : null}
                </Stack> : null}

                {editing && !loadingPool && selectablePlayers.length === 0 ? <Typography variant="body2" color="text.secondary">
                    No additional unassigned players are available for this sport.
                </Typography> : null}

                {editing && poolTotal > PLAYER_POOL_PAGE_SIZE ? <Pagination
                    count={Math.ceil(poolTotal / PLAYER_POOL_PAGE_SIZE)}
                    page={poolPage + 1}
                    onChange={(_, value) => loadAvailablePlayerPage(value - 1)}
                    disabled={loadingPool || busy}
                    size="small"
                    sx={{ alignSelf: 'center' }}
                /> : null}

                {assignedPlayers.length === 0 ? <Typography color="text.secondary">This team currently has no players.</Typography> : <Table size="small">
                    <TableHead><TableRow><TableCell>Player</TableCell><TableCell>Country / year</TableCell><TableCell>Lineup</TableCell>{editing ? <TableCell align="right">Remove</TableCell> : null}</TableRow></TableHead>
                    <TableBody>{assignedPlayers.map(player => {
                        const currentRole = assignments[player.id];
                        return <TableRow key={player.id} hover>
                            <TableCell>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar src={player.imageUrl ?? undefined} sx={{ width: 36, height: 36 }}>{player.name.slice(0, 1)}</Avatar>
                                    <Box><Typography variant="body2">{player.name}</Typography>{player.nameRu ? <Typography variant="caption" color="text.secondary">{player.nameRu}</Typography> : null}</Box>
                                </Stack>
                            </TableCell>
                            <TableCell>{player.originCountry} · {player.yearOfBirth}</TableCell>
                            <TableCell>{editing ? <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel>Lineup</InputLabel>
                                <Select
                                    label="Lineup"
                                    value={currentRole}
                                    onChange={event => setAssignments(current => ({
                                        ...current,
                                        [player.id]: event.target.value as VirtualLineupType,
                                    }))}
                                >
                                    {VIRTUAL_LINEUP_TYPES.map(role => <MenuItem
                                        key={role}
                                        value={role}
                                        disabled={role !== currentRole && Boolean(rosterPlan) && assignmentCounts[role] >= virtualRosterMaximum(rosterPlan, role)}
                                    >{virtualLineupLabel(role)}</MenuItem>)}
                                </Select>
                            </FormControl> : <Chip size="small" variant="outlined" label={virtualLineupLabel(currentRole)} />}</TableCell>
                            {editing ? <TableCell align="right"><Tooltip title="Remove from team">
                                <IconButton color="error" onClick={() => setAssignments(current => {
                                    const next = { ...current };
                                    delete next[player.id];
                                    return next;
                                })}><DeleteOutlineIcon /></IconButton>
                            </Tooltip></TableCell> : null}
                        </TableRow>;
                    })}</TableBody>
                </Table>}
            </Stack>}
        </DialogContent>
        <DialogActions>
            <Button onClick={editing ? cancelEditing : props.onClose} disabled={busy}>{editing ? 'Cancel' : 'Close'}</Button>
            {!editing ? <Button variant="outlined" onClick={regenerate} disabled={loading || busy}>
                {regenerating ? <CircularProgress size={20} /> : team.imageUrl ? 'Regenerate logo' : 'Generate logo'}
            </Button> : null}
            {editing ? <Button variant="contained" onClick={save} disabled={!valid || loadingPool || busy}>
                {saving ? <CircularProgress size={20} /> : 'Save changes'}
            </Button> : <Button variant="contained" onClick={startEditing} disabled={loading || !detailsLoaded || busy}>Edit team</Button>}
        </DialogActions>
    </Dialog>;
}
